import { ChevronRight, Hourglass, Play } from "lucide-react";
import { formatDuration } from "../executionTimeline";

// Una fila del timeline. Corridas y esperas comparten la grilla para que la
// vista se lea como una sola secuencia y no como dos listas entrelazadas.

// Los anchos viven acá y los importa la cabecera: dos definiciones se separan
// a la primera columna que se agregue.
export const COLS = {
  seq: "w-12",
  kind: "w-16",
  name: "min-w-0 flex-1",
  detail: "w-52",
  duration: "w-20",
  status: "w-24",
  cost: "w-16",
  chevron: "w-5",
};

const STATUS_BADGE = {
  completed: "bg-emerald-500/15 text-emerald-400",
  failed: "bg-rose-500/15 text-rose-400",
  running: "bg-blue-500/15 text-blue-400",
  resumed: "bg-blue-500/15 text-blue-300",
  flushed: "bg-blue-500/10 text-blue-300",
  waiting: "bg-violet-500/15 text-violet-300",
  expired: "bg-amber-500/15 text-amber-400",
  cancelled: "bg-zinc-700/40 text-zinc-400",
};

// De dónde salió el costo. `n/a` no es cero: es que nadie lo midió — pasa en
// los pasos que corren después de reanudar.
const COST_SOURCE = {
  estimated: "est.",
  external_assistant_call: "ext.",
  mixed: "mix",
};

const WAIT_DETAIL = {
  resumed: "resumed by event",
  flushed: "message burst flushed",
  expired: "timed out",
  cancelled: "cancelled while waiting",
  waiting: "waiting for the next event",
};

const detailOf = (entry) => {
  if (entry.kind === "wait") {
    const ending = WAIT_DETAIL[entry.status] || entry.status || "";
    return entry.reason ? `${ending} · ${entry.reason}` : ending;
  }
  if (entry.status === "failed") return entry.error?.message || "failed";
  return entry.retries ? `${entry.retries} retries` : "";
};

export default function TimelineRow({ entry, selected, onSelect }) {
  const isWaitRow = entry.kind === "wait";
  const Icon = isWaitRow ? Hourglass : Play;
  const coins = entry.cost?.coins;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(entry)}
      className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
        selected ? "bg-violet-500/10 ring-1 ring-inset ring-violet-500/40" : "hover:bg-white/5"
      }`}
    >
      <span className={`${COLS.seq} shrink-0 font-mono text-[10px] text-gray-600`}>{entry.seq}</span>

      <span className={`${COLS.kind} shrink-0`}>
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
            isWaitRow ? "bg-blue-500/15 text-blue-300" : "bg-emerald-500/15 text-emerald-400"
          }`}
        >
          <Icon className="h-2.5 w-2.5" />
          {isWaitRow ? "wait" : "step"}
        </span>
      </span>

      <span className={COLS.name}>
        <span className="block truncate text-xs text-gray-100">{entry.name || "—"}</span>
        <span className="block truncate text-[10px] text-gray-500">
          {entry.type || "—"}
          {entry.pass_number > 1 && (
            <span className="ml-1.5 text-violet-400">pass {entry.pass_number}</span>
          )}
        </span>
      </span>

      <span className={`${COLS.detail} shrink-0 truncate text-[10px] text-gray-500`}>
        {detailOf(entry)}
      </span>

      <span className={`${COLS.duration} shrink-0 text-right text-[11px] text-gray-300`}>
        {entry.duration_ms === null || entry.duration_ms === undefined ? (
          <span className="text-violet-300">open</span>
        ) : (
          formatDuration(entry.duration_ms)
        )}
      </span>

      <span className={`${COLS.status} shrink-0`}>
        <span
          className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${
            STATUS_BADGE[entry.status] || "bg-zinc-700/40 text-zinc-400"
          }`}
        >
          {entry.status || "—"}
        </span>
      </span>

      <span className={`${COLS.cost} shrink-0 text-right text-[10px]`}>
        {coins === null || coins === undefined ? (
          <span className="text-gray-700">—</span>
        ) : (
          <>
            <span className={coins > 0 ? "text-amber-300" : "text-gray-500"}>{coins}</span>{" "}
            <span className="text-gray-600">{COST_SOURCE[entry.cost.source] || "n/a"}</span>
          </>
        )}
      </span>

      <ChevronRight className={`${COLS.chevron} h-3.5 shrink-0 text-gray-700`} />
    </button>
  );
}
